/**
 * SMS Templates Tab
 * Displays and manages SMS templates
 */

import { useState, useMemo } from 'react';
import {
  IconTemplate,
  IconEye,
  IconEdit,
  IconTrash,
  IconPlus,
  IconToggleLeft,
  IconToggleRight,
} from '@tabler/icons-react';
import { Stack, Group, Text, Button, ActionIcon, Badge } from '../../../../../components/ui';
import { DataTable } from '../../../../../components/advanced/data';
import type { DataTableColumn } from '../../../../../components/advanced/data/DataTable';
import { EmptyState, Card } from '../../../../../components/ui/data-display';
import { alert } from '../../../../../components/ui/providers/AlertProvider';
import type { SmsTemplatesTabProps, SmsTemplateRow } from '../types';
import { mapTemplateToRow, getCharacterCount, decodeSmsMessage } from '../types';
import { formatDate } from '../../types';

export function SmsTemplatesTab({
  templates,
  loading,
  onRefresh,
  onView,
  onEdit,
  onDelete,
  onCreate,
}: SmsTemplatesTabProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const rows: SmsTemplateRow[] = useMemo(() => {
    return templates.map(mapTemplateToRow);
  }, [templates]);

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;

    const query = searchQuery.toLowerCase();
    return rows.filter((row) => {
      const decodedMessage = decodeSmsMessage(row.message);
      return (
        row.templateName?.toLowerCase().includes(query) ||
        decodedMessage.toLowerCase().includes(query)
      );
    });
  }, [rows, searchQuery]);

  const columns: DataTableColumn<SmsTemplateRow>[] = useMemo(
    () => [
      {
        key: 'templateName',
        label: 'Template Name',
        sortable: true,
        render: (value, row) => (
          <Group gap="xs">
            <IconTemplate size={16} />
            <Text size="sm" fw={500}>
              {value || 'Unnamed Template'}
            </Text>
          </Group>
        ),
      },
      {
        key: 'message',
        label: 'Message Preview',
        sortable: true,
        render: (value) => {
          const decodedMessage = decodeSmsMessage(value);
          const { count, segments } = getCharacterCount(decodedMessage);
          return (
            <Stack gap={4}>
              <Text size="sm" lineClamp={2}>
                {decodedMessage || '(No Message)'}
              </Text>
              <Group gap="xs">
                <Badge size="xs" variant="light">
                  {count} chars
                </Badge>
                <Badge size="xs" variant="light" color={segments > 1 ? 'orange' : 'green'}>
                  {segments} segment{segments > 1 ? 's' : ''}
                </Badge>
              </Group>
            </Stack>
          );
        },
      },
      {
        key: 'isActive',
        label: 'Status',
        sortable: true,
        filterable: true,
        render: (value) => (
          <Group gap="xs">
            {value ? (
              <>
                <IconToggleRight size={16} color="green" />
                <Badge color="green" variant="light">
                  Active
                </Badge>
              </>
            ) : (
              <>
                <IconToggleLeft size={16} color="gray" />
                <Badge color="gray" variant="light">
                  Inactive
                </Badge>
              </>
            )}
          </Group>
        ),
      },
      {
        key: 'createdDateTime',
        label: 'Created',
        sortable: true,
        render: (value) => <Text size="sm" c="dimmed">{formatDate(value)}</Text>,
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, row) => (
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                onView(row);
              }}
              title="View Template"
            >
              <IconEye size={16} />
            </ActionIcon>

            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row);
              }}
              title="Edit Template"
            >
              <IconEdit size={16} />
            </ActionIcon>

            <ActionIcon
              variant="subtle"
              color="red"
              onClick={async (e) => {
                e.stopPropagation();
                const confirmed = await alert.confirm(
                  'Delete Template',
                  `Delete the template "${row.templateName}"?`,
                  {
                    confirmText: 'Delete',
                  }
                );
                if (confirmed) {
                  await onDelete(row.templateID);
                }
              }}
              title="Delete Template"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [onView, onEdit, onDelete]
  );

  if (!loading && templates.length === 0) {
    return (
      <EmptyState
        title="No SMS Templates"
        description="You haven't created any SMS templates yet. Create your first template to get started."
        icon={<IconTemplate size={48} />}
        actionLabel="Create Template"
        onAction={onCreate}
      />
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {filteredRows.length} template(s)
        </Text>
        <Button
          leftSection={<IconPlus size={16} />}
          gradientVariant="secondary"
          onClick={onCreate}
        >
          Create Template
        </Button>
      </Group>

      <Card withBorder padding="md">
        <DataTable
          data={filteredRows}
          columns={columns}
          loading={loading}
          searchPlaceholder="Search by name or message..."
          searchValue={searchQuery}
          onSearch={setSearchQuery}
          onRowClick={(row) => onView(row)}
          pagination={{
            page: 1,
            pageSize: 50,
            total: filteredRows.length,
            onPageChange: () => {},
          }}
          gradientVariant="secondary"
          highlightOnHover
          striped
          withRowAnimations
          enableInternalSort
          enableInternalSearch
        />
      </Card>
    </Stack>
  );
}
